// src/pages/CheckoutPage.jsx
import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Layout from "../components/Layout";
import DepositCard from "../components/DepositCard";
import PageHeader from "../components/PageHeader";
import Button from "../components/Button";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { initiatePayment } from "../services/paymentService";
import { getDepositDetails } from "../services/depositService";
import { loadStripe } from "@stripe/stripe-js";
import config from "../config";
import logo from "../assets/logo-fast-caution.png";
import "./CheckoutPage.css";

function CheckoutPage() {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [depositDetails, setDepositDetails] = useState(null);
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const depositIdFromUrl = queryParams.get("id") || null;

  // Fetch deposit details
  const fetchDepositDetails = async (id) => {
    setLoading(true);
    try {
      const details = await getDepositDetails(id);
      setDepositDetails(details);
    } catch (error) {
      alert(t("checkout.fetchError", "Error fetching deposit details: ") + error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (depositIdFromUrl) {
      fetchDepositDetails(depositIdFromUrl);
    }
  }, [depositIdFromUrl]);

  const handlePayment = async () => {
    setLoading(true);
    try {
      const sessionId = await initiatePayment(depositDetails.depositAmount, depositDetails.depositId);
      const stripe = await loadStripe(config.STRIPE_PUBLIC_KEY);
      const result = await stripe.redirectToCheckout({ sessionId });
      if (result.error) {
        throw new Error(result.error.message);
      }
    } catch (error) {
      alert(t("checkout.paymentError", "Error during payment: ") + error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="checkout-container">
        <div className="checkout-top-image">
          <img src={logo} alt={t("checkout.topImageAlt", "Checkout Image")} />
        </div>
        <PageHeader 
          title={t("checkout.title")} 
          subtitle={t("checkout.subtitle")}
        />
        <DepositCard
          title={t("checkout.cardTitle")}
          loading={loading}
          depositDetails={depositDetails}
          onPayment={handlePayment}
          t={t}
        />
        <div className="checkout-disclaimer">
          <p className="checkout-footer-text-star">{t("checkout.disclaimerStar")}</p>
          <p className="checkout-footer-text-disclaimer">{t("checkout.disclaimerText")}</p>
        </div>
      </div>
    </Layout>
  );
}

export default CheckoutPage;
